import { css } from "@emotion/react"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { scaleValue } from "../ui/helpers/css"
import { Section } from "../../../../packages/editing/Section"
import { Localized } from "../../../../packages/localization/Localized"
import { useLocalize } from "../../../../packages/localization/client-side/useLocalize"
import { Markdown, Url } from "../../../../reactor"
import { Heading } from "../ui/components/typography/Heading"
import { Flex } from "../ui/components/base/Flex"
import { Text } from "../ui/components/typography/Text"
import { Button } from "../ui/components/buttons/Button"
import { useReviews } from "../client"
import { useRedoitTheme } from "../ui/theme"
import { TrustpilotWidget } from "../ui/components/other/TrustpilotWidget"

/**
 * @icon ui-message-heart-square
 */
function TrustpilotRateUs(section: {
    /**
     * Heading for the reviews section.
     * @default '{"en": "Rate us on Trustpilot"}'
     */
    heading: Localized<string>

    /**
     * Text to show below the heading.
     * @default '{"en": "Your opinion is important for us. We would love to hear from you to keep getting better. Help us to grow by leaving us a note on Trustpilot."}'
     */
    text?: Localized<Markdown>

    /**
     * Show button linking to Trustpilot review page.
     */
    cta?: {
        /**
         * @default '{"en": "Leave us a review"}'
         */
        text: Localized<string>

        /**
         * @default https://www.trustpilot.com
         */
        url: Url
    }

    /**
     * How fast the carousel scrolls. Given in pixels per second.
     *
     * @default 30
     */
    autoScrollSpeed: number
}) {
    const localize = useLocalize()
    const reviews = useReviews()
    const { helpers } = useRedoitTheme()
    const { responsiveCss } = helpers

    dayjs.extend(relativeTime)

    if (reviews.loading) return <></>

    return (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
            })}
        >
            <Flex
                backgroundColor="gray100"
                borderRadius="lg"
                css={css(
                    {
                        overflow: "hidden",
                        gap: 48,
                        alignItems: "flex-start",
                        flexDirection: "column",
                        padding: 32,
                    },
                    responsiveCss("min", "md", {
                        padding: 64,
                        gap: 64,
                        alignItems: "center",
                        flexDirection: "row",
                        height: 600,
                    }),
                    responsiveCss("min", "lg", {
                        paddingLeft: 96,
                        paddingRight: 96,
                        gap: 112,
                    })
                )}
            >
                <Flex
                    direction="column"
                    alignItems="flex-start"
                    style={{ minWidth: 0, flex: 1 }}
                    css={responsiveCss("max", "sm", { paddingLeft: 24, paddingRight: 24 })}
                >
                    <div css={responsiveCss("max", "sm", { display: "none" })}>
                        <TrustpilotWidget widget="mini" width={160} />
                    </div>
                    <Flex
                        direction="column"
                        gap={scaleValue(24)}
                        css={responsiveCss("max", "sm", {
                            alignItems: "center",
                            textAlign: "center",
                        })}
                    >
                        <Heading level={1}>{localize(section.heading)}</Heading>
                        {section.text && (
                            <Text variant="body" size="lg" color="gray400">
                                {localize(section.text)}
                            </Text>
                        )}
                    </Flex>
                    {section.cta ? (
                        <Flex
                            margin={{ top: "sm" }}
                            css={responsiveCss("max", "sm", {
                                justifyContent: "center",
                                width: "100%",
                            })}
                        >
                            <Button
                                as="a"
                                href={section.cta.url.valueOf()}
                                target="_blank"
                                variant="primary"
                                css={responsiveCss("max", "xs", { width: "100%" })}
                            >
                                {localize(section.cta.text)}
                            </Button>
                        </Flex>
                    ) : null}
                </Flex>
                <Flex
                    flex={1}
                    css={css(
                        { width: "100%", justifyContent: "center", marginBottom: 16 },
                        responsiveCss("min", "md", {
                            display: "none",
                        })
                    )}
                >
                    <TrustpilotWidget widget="carousel" />
                </Flex>
                <Flex
                    flex={1}
                    css={responsiveCss("max", "sm", {
                        display: "none",
                    })}
                >
                    <div style={{ width: "100%", overflow: "hidden", height: 500 }}>
                        <div style={{ width: "100%", transform: "translateY(-120px)" }}>
                            <TrustpilotWidget widget="grid" height={620} stars={[4, 5]} />
                        </div>
                    </div>
                </Flex>
            </Flex>
        </div>
    )
}

Section(TrustpilotRateUs)
